import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaArrowUp, FaTimes, FaSearch, FaFolder, FaFolderOpen } from 'react-icons/fa';
import './GerenciarCategorias.css';
import { normalizeText } from '../utils/text';

function GerenciarCategorias() {
  const [categoryName, setCategoryName] = useState('');
  const [icon, setIcon] = useState('');
  const [subcategoryName, setSubcategoryName] = useState('');
  const [parentCategory, setParentCategory] = useState('');
  const [parentSubcategory, setParentSubcategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [loading, setLoading] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubcategoryModalOpen, setIsSubcategoryModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCategories(categories);
    } else {
      const lowerQuery = normalizeText(searchQuery);
      const filtered = categories.filter(category =>
        normalizeText(category.name).includes(lowerQuery)
      );
      setFilteredCategories(filtered);
    }
  }, [searchQuery, categories]);

  const fetchCategories = async () => {
    const ref = collection(db, 'categories');
    const snapshot = await getDocs(ref);
    const fetchedCategories = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setCategories(fetchedCategories);
    setFilteredCategories(fetchedCategories);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setMensagem('');
    setLoading(true);

    try {
      await addDoc(collection(db, 'categories'), {
        name: categoryName,
        icon: icon || 'FaLayerGroup',
        subcategories: []
      });
      setMensagem('Categoria cadastrada com sucesso!');
      setCategoryName('');
      setIcon('');
      fetchCategories();
    } catch (error) {
      setMensagem('Erro: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubcategorySubmit = async (e) => {
    e.preventDefault();
    setMensagem('');
    setLoading(true);

    try {
      const categoryDoc = doc(db, 'categories', parentCategory);
      const category = categories.find(cat => cat.id === parentCategory);
      let updatedSubcategories = [...category.subcategories];

      if (!parentSubcategory) {
        updatedSubcategories.push({ name: subcategoryName, subcategories: [] });
      } else {
        const findSubcategory = (subs, name) => {
          for (let sub of subs) {
            if (sub.name === name) return sub;
            if (sub.subcategories) {
              const found = findSubcategory(sub.subcategories, name);
              if (found) return found;
            }
          }
          return null;
        };

        const parent = findSubcategory(updatedSubcategories, parentSubcategory);
        if (parent) {
          parent.subcategories = parent.subcategories || [];
          parent.subcategories.push({ name: subcategoryName, subcategories: [] });
        }
      }

      await updateDoc(categoryDoc, { subcategories: updatedSubcategories });
      setMensagem('Subcategoria cadastrada com sucesso!');
      setSubcategoryName('');
      setParentCategory('');
      setParentSubcategory('');
      fetchCategories();
    } catch (error) {
      setMensagem('Erro: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = async (categoryId, newName, newIcon) => {
    setMensagem('');
    try {
      const categoryDoc = doc(db, 'categories', categoryId);
      await updateDoc(categoryDoc, {
        name: newName,
        icon: newIcon || 'FaLayerGroup'
      });
      setMensagem('Categoria atualizada com sucesso!');
      fetchCategories();
      setIsModalOpen(false);
    } catch (error) {
      setMensagem('Erro: ' + error.message);
    }
  };

  const handleEditSubcategory = async (categoryId, path, newName) => {
    setMensagem('');
    try {
      const categoryDoc = doc(db, 'categories', categoryId);
      const category = categories.find(cat => cat.id === categoryId);
      let updatedSubcategories = [...category.subcategories];

      let currentLevel = updatedSubcategories;
      for (let i = 0; i < path.length - 1; i++) {
        currentLevel = currentLevel[path[i]].subcategories;
      }
      currentLevel[path[path.length - 1]].name = newName;

      await updateDoc(categoryDoc, { subcategories: updatedSubcategories });
      setMensagem('Subcategoria atualizada com sucesso!');
      fetchCategories();
      setEditingSubcategory(null);
    } catch (error) {
      setMensagem('Erro: ' + error.message);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Deseja realmente excluir esta categoria e suas subcategorias?')) return;
    setMensagem('');
    try {
      await deleteDoc(doc(db, 'categories', categoryId));
      setMensagem('Categoria excluída com sucesso!');
      fetchCategories();
    } catch (error) {
      setMensagem('Erro: ' + error.message);
    }
  };

  const handleDeleteSubcategory = async (categoryId, path) => {
    if (!window.confirm('Deseja realmente excluir esta subcategoria e suas subcategorias filhas?')) return;
    setMensagem('');
    try {
      const categoryDoc = doc(db, 'categories', categoryId);
      const category = categories.find(cat => cat.id === categoryId);
      let updatedSubcategories = [...category.subcategories];

      let currentLevel = updatedSubcategories;
      for (let i = 0; i < path.length - 1; i++) {
        currentLevel = currentLevel[path[i]].subcategories;
      }
      currentLevel.splice(path[path.length - 1], 1);

      await updateDoc(categoryDoc, { subcategories: updatedSubcategories });
      setMensagem('Subcategoria excluída com sucesso!');
      fetchCategories();
    } catch (error) {
      setMensagem('Erro: ' + error.message);
    }
  };

  const openSubcategoryModal = (category) => {
    setSelectedCategory(category);
    setIsSubcategoryModalOpen(true);
  };

  const renderSubcategories = (subcategories, categoryId, level = 1, path = []) => {
    return subcategories.map((sub, index) => {
      const currentPath = [...path, index];
      const isEditing = editingSubcategory && editingSubcategory.path.join('-') === currentPath.join('-');
      return (
        <div key={index} className={`subcategory-item level-${level}`}>
          <div className="subcategory-content">
            {isEditing ? (
              <div className="edit-subcategory">
                <input
                  type="text"
                  value={editingSubcategory.name}
                  onChange={(e) => setEditingSubcategory({ ...editingSubcategory, name: e.target.value })}
                  className="edit-input"
                />
                <button
                  onClick={() => handleEditSubcategory(categoryId, currentPath, editingSubcategory.name)}
                  className="save-subcategory"
                >
                  <FaEdit className="icon" /> Salvar
                </button>
                <button
                  onClick={() => setEditingSubcategory(null)}
                  className="cancel-subcategory"
                >
                  <FaTimes className="icon" /> Cancelar
                </button>
              </div>
            ) : (
              <>
                <span className="subcategory-name">
                  <FaFolder className="icon" /> {sub.name}
                </span>
                <span className="subcategory-actions">
                  <button
                    onClick={() => setEditingSubcategory({ path: currentPath, name: sub.name })}
                    className="editar"
                  >
                    <FaEdit className="icon" /> Editar
                  </button>
                  <button
                    onClick={() => handleDeleteSubcategory(categoryId, currentPath)}
                    className="deletar"
                  >
                    <FaTrash className="icon" /> Deletar
                  </button>
                </span>
              </>
            )}
          </div>
          {sub.subcategories?.length > 0 && (
            <div className="subcategory-nested">
              {renderSubcategories(sub.subcategories, categoryId, level + 1, currentPath)}
            </div>
          )}
        </div>
      );
    });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="gerenciar-categorias admin-panel-container">
      <header className="admin-header">
        <button className="back-button" onClick={() => navigate('/admin-panel')}>
          <FaArrowLeft className="icon" /> Voltar
        </button>
        <div className="header-title">
          <h1>Gerenciar Categorias</h1>
          <p>Conecta EAD - Controle de categorias e subcategorias</p>
        </div>
      </header>

      <div className="admin-content-wrapper">
        <div className="admin-content">
          <div className="admin-form-card">
            <h2>Adicionar Nova Categoria</h2>
            <form onSubmit={handleCategorySubmit}>
              <div className="form-group">
                <label htmlFor="categoryName">Nome da Categoria</label>
                <input
                  type="text"
                  id="categoryName"
                  placeholder="Digite o nome da categoria"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="icon">Ícone (ex.: FaLayerGroup)</label>
                <input
                  type="text"
                  id="icon"
                  placeholder="Digite o nome do ícone"
                  value={icon}
                  onChange={(e) => setIcon(e.target.value)}
                />
              </div>
              <button type="submit" disabled={loading}>
                {loading ? 'Cadastrando...' : (
                  <>
                    <FaPlus className="icon" /> Adicionar Categoria
                  </>
                )}
              </button>
            </form>
          </div>

          <div className="admin-form-card">
            <h2>Adicionar Subcategoria</h2>
            <form onSubmit={handleSubcategorySubmit}>
              <div className="form-group">
                <label htmlFor="parentCategory">Categoria Pai</label>
                <select
                  id="parentCategory"
                  value={parentCategory}
                  onChange={(e) => {
                    setParentCategory(e.target.value);
                    setParentSubcategory('');
                  }}
                  required
                >
                  <option value="">Selecione uma categoria</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              {parentCategory && (
                <>
                  <div className="form-group">
                    <label htmlFor="parentSubcategory">Subcategoria Pai</label>
                    <select
                      id="parentSubcategory"
                      value={parentSubcategory}
                      onChange={(e) => setParentSubcategory(e.target.value)}
                    >
                      <option value="">Nenhuma (Nível 1)</option>
                      {categories
                        .find(cat => cat.id === parentCategory)
                        ?.subcategories.flatMap((sub, index, arr) => {
                          const options = [{ name: sub.name, path: [sub.name] }];
                          if (sub.subcategories?.length > 0) {
                            const nested = sub.subcategories.map(nSub => ({
                              name: nSub.name,
                              path: [sub.name, nSub.name]
                            }));
                            return [...options, ...nested];
                          }
                          return options;
                        })
                        .map(opt => (
                          <option key={opt.path.join('-')} value={opt.path.join('/')}>{opt.name}</option>
                        ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="subcategoryName">Nome da Subcategoria</label>
                    <input
                      type="text"
                      id="subcategoryName"
                      placeholder="Digite o nome da subcategoria"
                      value={subcategoryName}
                      onChange={(e) => setSubcategoryName(e.target.value)}
                      required
                    />
                  </div>
                </>
              )}
              <button type="submit" disabled={loading || !parentCategory}>
                {loading ? 'Cadastrando...' : (
                  <>
                    <FaPlus className="icon" /> Adicionar Subcategoria
                  </>
                )}
              </button>
            </form>
          </div>

          {mensagem && (
            <div className={`mensagem ${mensagem.includes('Erro') ? 'erro' : 'sucesso'}`}>
              {mensagem}
            </div>
          )}

          <div className="admin-list">
            <div className="list-header">
              <h2>Lista de Categorias</h2>
              <div className="search-container">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Buscar categorias..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
            <div className="admin-table">
              <div className="table-header">
                <span>Nome</span>
                <span>Ícone</span>
                <span>Subcategorias</span>
                <span>Ações</span>
              </div>
              {filteredCategories.map(category => (
                <div key={category.id} className="table-row">
                  <span><FaFolderOpen className="icon" /> {category.name}</span>
                  <span>{category.icon}</span>
                  <span>{category.subcategories.length}</span>
                  <span className="actions">
                    <button
                      onClick={() => {
                        setSelectedCategory(category);
                        setIsModalOpen(true);
                      }}
                      className="editar"
                    >
                      <FaEdit className="icon" /> Editar
                    </button>
                    <button
                      onClick={() => openSubcategoryModal(category)}
                      className="gerenciar-subcategorias"
                    >
                      <FaFolder className="icon" /> Gerenciar Subcategorias
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="deletar"
                    >
                      <FaTrash className="icon" /> Deletar
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && selectedCategory && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setIsModalOpen(false)}>
              <FaTimes className="icon" />
            </button>
            <h2>Editar Categoria</h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleEditCategory(selectedCategory.id, categoryName, icon);
            }}>
              <div className="form-group">
                <label htmlFor="editCategoryName">Nome da Categoria</label>
                <input
                  type="text"
                  id="editCategoryName"
                  placeholder="Digite o nome da categoria"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="editIcon">Ícone (ex.: FaLayerGroup)</label>
                <input
                  type="text"
                  id="editIcon"
                  placeholder="Digite o nome do ícone"
                  value={icon}
                  onChange={(e) => setIcon(e.target.value)}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="cancelar"
                >
                  <FaTimes className="icon" /> Cancelar
                </button>
                <button type="submit">
                  <FaEdit className="icon" /> Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isSubcategoryModalOpen && selectedCategory && (
        <div className="modal-overlay">
          <div className="modal-content subcategory-modal">
            <button className="modal-close" onClick={() => setIsSubcategoryModalOpen(false)}>
              <FaTimes className="icon" />
            </button>
            <h2>Gerenciar Subcategorias de "{selectedCategory.name}"</h2>
            <div className="subcategory-list">
              {selectedCategory.subcategories.length > 0 ? (
                renderSubcategories(selectedCategory.subcategories, selectedCategory.id)
              ) : (
                <p className="no-subcategories">Nenhuma subcategoria encontrada.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {showBackToTop && (
        <button className="back-to-top" onClick={scrollToTop}>
          <FaArrowUp className="icon" /> Voltar ao Topo
        </button>
      )}
    </div>
  );
}

export default GerenciarCategorias;
